import React, { useState } from 'react';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import Table from 'components/Table';
import { makeStyles } from '@material-ui/styles';
import { TableHead } from '@material-ui/core';
import Icons from 'components/Icons';
import api from 'api';
import { Modal, Prompt } from 'components/Base';
import Forms from 'components/Forms';
import { PolicyType } from 'store/modules/Policies/types';
import { FormValues } from 'components/Forms/v2/AdviserCharges/InitialCharge';
import { formatPercent, formatCurrency, Notify } from 'utils';
import { ChargeApplied, ChargeBase } from 'store/modules/AdvisorCharges/types';
import Store from 'store/store';
import { generateErrMesage } from 'consts/errorCodes';

const { connect, actions } = Store;
const { TableBody, TableRow, TableCell } = Table;

const useStyles = makeStyles({
  container: {
    margin: '2rem 0'
  },
  th: {
    fontWeight: 'bold',
    color: '#2F2F2F'
  },
  actionCell: {
    textAlign: 'end'
  },
  icon: {
    cursor: 'pointer',
    margin: '0 0.25rem'
  }
});

interface Props {
  portfolio: string;
  policies: PolicyType[];
  activePolicy: PolicyType;
  refreshPolicyData: () => Promise<any>;
}

const humanizeCharge = (chargeType: ChargeBase) => {
  switch (chargeType) {
    case ChargeBase.INITIAL:
      return 'Initial charge';
    case ChargeBase.INITIAL_REGULAR:
      return (
        <>
          <span>Initial charge</span>
          <br />
          <span>(regular contributions)</span>
        </>
      );
    case ChargeBase.ONGOING:
      return 'Ongoing charge';
    default:
      return '';
  }
};

const Charges = ({
  portfolio,
  activePolicy,
  policies,
  refreshPolicyData
}: Props) => {
  const [deleteChargeAction, setDeleteChargeAction] = useState<{
    isOpen: boolean;
    chargeId: null | number;
    isProcessing: boolean;
  }>({
    isOpen: false,
    chargeId: null,
    isProcessing: false
  });
  const [editChargeAction, setEditeChargeAction] = useState<{
    isOpen: boolean;
    chargeData: null | ChargeApplied;
    isProcessing: boolean;
    type: ChargeBase | null;
  }>({
    isOpen: false,
    chargeData: null,
    isProcessing: false,
    type: null
  });
  const classes = useStyles();

  const deleteCharge = (chargeId: any) => {
    return api
      .deleteCharge({
        portfolio: portfolio,
        chargeId: chargeId
      })
      .then(() => {
        refreshPolicyData().then(() => {
          Notify.success('Charge deleted');
          setDeleteChargeAction({
            isOpen: false,
            chargeId: null,
            isProcessing: false
          });
        });
      })
      .catch(err => {
        generateErrMesage(err);
      });
  };

  const prepareInitialData = (data: ChargeApplied): FormValues => {
    const areChargeExist = data.applied_charges.length > 0;
    const isPaidDirectly =
      areChargeExist && data.applied_charges[0].paid_directly;

    const isRegularCharge = data.charge_type === 3;
    const isChargeInCash = !!data.amount;

    return {
      payment_type: isRegularCharge ? 'regular_contribution' : 'lump_sum',
      charge_in_type: isChargeInCash ? 'monetary' : 'percentage',
      amount: formatCurrency(data.amount),
      percentage_value: formatPercent(Number(data.percentage_value) * 100),
      payment_method: isPaidDirectly ? 'paid_directly' : 'from_policy',
      applied_charges: {
        policy_id:
          areChargeExist && !isPaidDirectly
            ? (data.applied_charges[0].policy_id as number).toString()
            : null
      }
    };
  };
  const { advisor_charges_definitions: chargesApplied } = activePolicy;

  const isAnyChargeApplied = chargesApplied.length > 0;
  const isEditChargeOpen = editChargeAction.isOpen;

  return isAnyChargeApplied ? (
    <div className={classes.container}>
      <Modal
        isOpen={isEditChargeOpen}
        disableEscapeKeyDown
        disableBackdropClick
        closeModalFunc={() =>
          setEditeChargeAction({
            isOpen: false,
            chargeData: null,
            isProcessing: false,
            type: null
          })
        }
      >
        {editChargeAction.type === ChargeBase.INITIAL && (
          <Forms.v2.AdviserCharges.Initial
            policies={policies}
            activePolicy={activePolicy}
            portfolio={portfolio}
            isEdit={true}
            editedCharge={
              editChargeAction.chargeData
                ? editChargeAction.chargeData.id
                : undefined
            }
            closeModal={() =>
              setEditeChargeAction({
                isOpen: false,
                chargeData: null,
                isProcessing: false,
                type: null
              })
            }
            cta={refreshPolicyData}
            initialValues={prepareInitialData(
              editChargeAction.chargeData as ChargeApplied
            )}
          />
        )}
        {editChargeAction.type === ChargeBase.INITIAL_REGULAR && (
          <Forms.v2.AdviserCharges.Initial
            policies={policies}
            activePolicy={activePolicy}
            portfolio={portfolio}
            isEdit={true}
            editedCharge={
              editChargeAction.chargeData
                ? editChargeAction.chargeData.id
                : undefined
            }
            closeModal={() =>
              setEditeChargeAction({
                isOpen: false,
                chargeData: null,
                isProcessing: false,
                type: null
              })
            }
            cta={refreshPolicyData}
            initialValues={prepareInitialData(
              editChargeAction.chargeData as ChargeApplied
            )}
          />
        )}
        {editChargeAction.type === ChargeBase.ONGOING && (
          <Forms.v2.AdviserCharges.Ongoing
            policies={policies}
            activePolicy={activePolicy}
            portfolio={portfolio}
            isEdit={true}
            editedCharge={
              editChargeAction.chargeData
                ? editChargeAction.chargeData.id
                : undefined
            }
            closeModal={() =>
              setEditeChargeAction({
                isOpen: false,
                chargeData: null,
                isProcessing: false,
                type: null
              })
            }
            cta={refreshPolicyData}
            initialValues={prepareInitialData(
              editChargeAction.chargeData as ChargeApplied
            )}
          />
        )}
      </Modal>
      <Prompt
        isOpen={deleteChargeAction.isOpen}
        title={`Are you sure you want to delete this charge?`}
        confirmAction={() => deleteCharge(deleteChargeAction.chargeId)}
        isProcessing={deleteChargeAction.isProcessing}
        cancelAction={() =>
          setDeleteChargeAction({
            isOpen: false,
            chargeId: null,
            isProcessing: false
          })
        }
      />
      <Table.Table>
        <TableHead>
          <TableRow>
            <TableCell width="180px" className={classes.th}>
              Charge type
            </TableCell>
            <TableCell width="100px" className={classes.th}>
              Value
            </TableCell>
            <TableCell className={classes.th}>Payment method</TableCell>
            <TableCell className={classes.th}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {chargesApplied.map(charge => {
            const isPaidDirectly = charge.applied_charges
              ? charge.applied_charges.some(charge => charge.paid_directly)
              : false;

            const isChargeInCash = !!charge.amount;

            return (
              <TableRow key={charge.id}>
                <TableCell>{humanizeCharge(charge.charge_type)}</TableCell>
                <TableCell>
                  {isChargeInCash
                    ? formatCurrency(charge.amount)
                    : formatPercent(Number(charge.percentage_value) * 100)}
                </TableCell>
                <TableCell>
                  {isPaidDirectly ? 'Paid directly' : 'Paid from policy'}
                </TableCell>
                <TableCell className={classes.actionCell}>
                  <Icons.Edit
                    className={classes.icon}
                    onClick={() =>
                      setEditeChargeAction({
                        isOpen: true,
                        chargeData: charge,
                        isProcessing: false,
                        type: charge.charge_type
                      })
                    }
                  />
                  <DeleteOutlineIcon
                    onClick={() =>
                      setDeleteChargeAction({
                        isOpen: true,
                        chargeId: charge.id,
                        isProcessing: false
                      })
                    }
                    className={classes.icon}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table.Table>
    </div>
  ) : null;
};

export default connect(null, {
  updatePolicy: actions.caseActions.updatePolicies
})(Charges);
